import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { Seal } from "@/components/brand/Seal";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { assertWritable } from "@/lib/viewAs";
import { useAuth } from "@/hooks/useAuth";
import { ArrowRight, ShieldCheck, GraduationCap, PiggyBank, Landmark, MessageCircle } from "lucide-react";

export const Route = createFileRoute("/client/goals")({
  head: () => ({ meta: [{ title: "Family Goals — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Command Center" nav={CLIENT_NAV} tourKey="client_tour">
        <Goals />
      </PortalShell>
    </ProtectedRoute>
  ),
});

type Pillar = "protection" | "education" | "retirement" | "legacy";

const GOAL_CARDS: {
  key: Pillar;
  label: string;
  caption: string;
  why: string;
  ctaLabel: string;
  to: string;
  Icon: typeof ShieldCheck;
}[] = [
  {
    key: "protection",
    label: "Protection",
    caption: "Income, life, and disability coverage in force.",
    why: "If income stops tomorrow, this is what keeps the household whole. Start here — everything else compounds on top.",
    ctaLabel: "Review my policies",
    to: "/client/policies",
    Icon: ShieldCheck,
  },
  {
    key: "education",
    label: "Education",
    caption: "Funding paths for the next generation.",
    why: "Tuition inflation outruns wage growth. A funded plan today turns into options — not debt — for your kids.",
    ctaLabel: "Open Academy",
    to: "/client/education",
    Icon: GraduationCap,
  },
  {
    key: "retirement",
    label: "Retirement",
    caption: "Income plan for the second act.",
    why: "Retirement is an income problem, not a savings number. We'll map the years between work and Social Security.",
    ctaLabel: "Run the calculator",
    to: "/tools/retirement",
    Icon: PiggyBank,
  },
  {
    key: "legacy",
    label: "Legacy",
    caption: "Estate, trusts, and generational transfer.",
    why: "Without instructions, assets get taxed, contested, or lost. The Vault is where your family finds the answers.",
    ctaLabel: "Open the Vault",
    to: "/client/vault",
    Icon: Landmark,
  },
];

const CHECKLIST: { item: string; pillar: Pillar; rationale: string }[] = [
  { item: "Confirm primary and contingent beneficiaries on every policy", pillar: "protection", rationale: "Beneficiary forms override wills. One outdated line can redirect a payout away from your family." },
  { item: "Review annual income-replacement coverage", pillar: "protection", rationale: "Rule of thumb: 10–15× annual income. We'll confirm yours is still right as your household changes." },
  { item: "Set or revisit education funding goals", pillar: "education", rationale: "Even $100/mo started early can cover a meaningful share of in-state tuition. Set the target, then automate." },
  { item: "Schedule annual stewardship review with Strategist", pillar: "retirement", rationale: "Once a year we recheck contributions, beneficiaries, and the income plan. It takes 30 minutes." },
  { item: "Locate and digitize household estate documents", pillar: "legacy", rationale: "Will, trust, POA, healthcare directive. Upload to the Vault so they're not lost in a filing cabinet." },
  { item: "Walk spouse or executor through vault access", pillar: "legacy", rationale: "The plan only works if someone other than you can find it. Share access while you're well." },
  { item: "Confirm trustee and successor trustee assignments", pillar: "legacy", rationale: "Name a backup. Trustees move, age, and decline. A successor avoids court intervention." },
];

const PILLAR_META: Record<Pillar, { label: string; tone: string }> = {
  protection: { label: "Protection", tone: "bg-navy-800 text-cream-50" },
  education: { label: "Education", tone: "bg-bronze-600 text-navy-800" },
  retirement: { label: "Retirement", tone: "bg-navy-800 text-cream-50" },
  legacy: { label: "Legacy", tone: "bg-bronze-600 text-navy-800" },
};

function Goals() {
  const { user } = useAuth();
  const [clientId, setClientId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from("clients").select("id, family_checklist").eq("user_id", user.id).maybeSingle();
      if (data) {
        setClientId(data.id);
        setChecked(((data as any).family_checklist?.items ?? {}) as Record<string, boolean>);
      }
      setLoading(false);
    })();
  }, [user?.id]);

  async function toggle(item: string, value: boolean) {
    if (!assertWritable()) return;
    const next = { ...checked, [item]: value };
    setChecked(next);
    if (!clientId || !user) return;
    const payload = { items: next, updated_at: new Date().toISOString() };
    await supabase.from("clients").update({ family_checklist: payload }).eq("id", clientId);
    await supabase.from("activity_logs").insert({
      user_id: user.id, action: "checklist.updated", target_type: "client", target_id: clientId,
      metadata: { item, value },
    });
  }

  const completed = Object.values(checked).filter(Boolean).length;
  const nextItem = CHECKLIST.find(c => !checked[c.item]);

  return (
    <PortalPage
      eyebrow="Generational Goals"
      title="Goals, in step with your Strategist"
      intro="Four pillars of household stewardship and a living checklist your Strategist can see."
    >
      {nextItem && !loading && (
        <div className="bg-navy-800 text-cream-50 rounded-[4px] p-6 shadow-soft flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-400">Your next best step</p>
            <h3 className="mt-1 font-display text-[20px] leading-snug">{nextItem.item}</h3>
            <p className="mt-1 font-sans text-[13px] text-cream-50/80 max-w-2xl">{nextItem.rationale}</p>
          </div>
          <button
            onClick={() => toggle(nextItem.item, true)}
            className="self-start md:self-auto inline-flex items-center gap-2 px-5 py-3 bg-bronze-600 hover:bg-bronze-400 text-navy-800 font-sans font-semibold text-[12px] uppercase tracking-[0.16em] rounded-[2px] transition"
          >
            Mark complete <ArrowRight size={14} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {GOAL_CARDS.map(g => {
          const Icon = g.Icon;
          return (
            <div key={g.key} className="bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft flex flex-col">
              <div className="flex items-start gap-4">
                <Seal title={g.label[0]} subtitle={g.label} size="sm" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-bronze-600" />
                    <h3 className="font-display text-navy-800 text-[20px]">{g.label}</h3>
                  </div>
                  <p className="mt-1 font-sans text-[12px] text-charcoal-500">{g.caption}</p>
                </div>
              </div>
              <p className="mt-4 font-sans text-[13px] leading-relaxed text-charcoal-900 italic border-l-2 border-bronze-600/40 pl-4">
                {g.why}
              </p>
              <Link
                to={g.to}
                className="mt-5 inline-flex items-center gap-2 self-start font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600 hover:text-bronze-400 transition"
              >
                {g.ctaLabel} <ArrowRight size={14} />
              </Link>
            </div>
          );
        })}
      </div>

      <div className="mt-10 bg-white border border-bronze-600/20 rounded-[4px] shadow-soft">
        <div className="p-6 border-b border-bronze-600/20 flex items-center justify-between">
          <div>
            <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-600">Family Planning Checklist</p>
            <h3 className="mt-1 font-display text-navy-800 text-[20px]">{completed} of {CHECKLIST.length} complete</h3>
            <p className="mt-1 font-sans text-[12px] text-charcoal-500 max-w-xl">
              Each item below is grouped by the pillar it strengthens. Check them as you finish — your Strategist sees the same view.
            </p>
          </div>
          <div className="hidden md:flex flex-col items-end">
            <div className="w-32 h-2 bg-cream-50 rounded-full overflow-hidden border border-bronze-600/20">
              <div className="h-full bg-bronze-600 transition-all" style={{ width: `${(completed / CHECKLIST.length) * 100}%` }} />
            </div>
            <span className="mt-1 font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
              {Math.round((completed / CHECKLIST.length) * 100)}% complete
            </span>
          </div>
        </div>
        {loading ? <p className="p-6 font-sans text-charcoal-500">Loading…</p> : (
          <ul>
            {CHECKLIST.map(({ item, pillar, rationale }) => (
              <li key={item} className="flex items-start gap-3 p-5 border-b border-bronze-600/10 last:border-0">
                <Checkbox checked={!!checked[item]} onCheckedChange={(v) => toggle(item, v === true)} className="border-bronze-600 mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`font-sans text-[14px] font-medium ${checked[item] ? "line-through text-charcoal-500" : "text-charcoal-900"}`}>
                      {item}
                    </span>
                    <span className={`inline-block font-sans text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 ${PILLAR_META[pillar].tone}`} style={{ borderRadius: 2 }}>
                      {PILLAR_META[pillar].label}
                    </span>
                  </div>
                  <p className={`mt-1 font-sans text-[12px] leading-relaxed ${checked[item] ? "text-charcoal-500" : "text-charcoal-500"}`}>
                    {rationale}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-10 bg-cream-50 border border-bronze-600/30 rounded-[4px] p-6 shadow-soft flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-600">Stuck on a step?</p>
          <h3 className="mt-1 font-display text-navy-800 text-[20px]">Your Strategist can walk you through any item.</h3>
          <p className="mt-1 font-sans text-[13px] text-charcoal-500 max-w-2xl">
            No item on this list is too small. Send a question, share a document, or book a 30-minute review.
          </p>
        </div>
        <Link
          to="/client/messages"
          className="self-start md:self-auto inline-flex items-center gap-2 px-5 py-3 bg-navy-800 hover:bg-navy-700 text-cream-50 font-sans font-semibold text-[12px] uppercase tracking-[0.16em] rounded-[2px] transition"
        >
          <MessageCircle size={14} /> Message Strategist
        </Link>
      </div>

      <p className="mt-6 font-sans text-[11px] text-charcoal-500 italic">
        Educational only — not legal, tax, or investment advice. Policy benefits are governed by the actual contract issued by the carrier.
      </p>
    </PortalPage>
  );
}
